import { Injectable, signal, computed } from '@angular/core';
import { DarkModePreference, STORAGE_KEYS } from './theme.models';

@Injectable({ providedIn: 'root' })
export class DarkModeService {
  private readonly mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

  /** Signal holding the current mode: 'dark' | 'light' */
  readonly mode = signal<DarkModePreference>('light');

  /** Whether dark mode is currently active */
  readonly isDark = computed(() => this.mode() === 'dark');

  constructor() {
    this.initialize();
    this.listenToSystemChanges();
  }

  /** Toggle between dark and light mode */
  toggle(): void {
    const newMode: DarkModePreference = this.mode() === 'dark' ? 'light' : 'dark';
    this.setMode(newMode);
  }

  /** Set a specific mode */
  setMode(mode: DarkModePreference): void {
    this.mode.set(mode);
    this.applyMode(mode);
    this.persistMode(mode);
  }

  /** Initialize mode from localStorage or system preference */
  private initialize(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.DARK_MODE);
      if (stored === 'dark' || stored === 'light') {
        this.mode.set(stored);
        this.applyMode(stored);
        return;
      }
    } catch {
      // localStorage unavailable — fall through to OS preference
    }

    const osPrefersDark = this.mediaQuery.matches;
    const mode: DarkModePreference = osPrefersDark ? 'dark' : 'light';
    this.mode.set(mode);
    this.applyMode(mode);
  }

  /** Listen for OS color scheme changes */
  private listenToSystemChanges(): void {
    this.mediaQuery.addEventListener('change', (event: MediaQueryListEvent) => {
      // Only update if no user preference exists in localStorage
      try {
        const stored = localStorage.getItem(STORAGE_KEYS.DARK_MODE);
        if (stored === 'dark' || stored === 'light') {
          return; // User has explicit preference — ignore OS change
        }
      } catch {
        // localStorage unavailable — respect OS change
      }

      const mode: DarkModePreference = event.matches ? 'dark' : 'light';
      this.mode.set(mode);
      this.applyMode(mode);
    });
  }

  /** Apply the dark class and color-scheme to document.documentElement */
  private applyMode(mode: DarkModePreference): void {
    if (mode === 'dark') {
      document.documentElement.classList.add('dark');
      document.documentElement.style.colorScheme = 'dark';
    } else {
      document.documentElement.classList.remove('dark');
      document.documentElement.style.colorScheme = 'light';
    }
  }

  /** Persist mode to localStorage (graceful failure) */
  private persistMode(mode: DarkModePreference): void {
    try {
      localStorage.setItem(STORAGE_KEYS.DARK_MODE, mode);
    } catch {
      // Graceful failure — localStorage may be unavailable (private browsing, quota exceeded)
    }
  }
}
